import {EventBus} from '@mintdata-wsp/common/bus/EventBus';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {InitEvent, SyncValueEvent} from '@mintdata-wsp/wsp/controllers/multiplayer/MultiplayerController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Category, Logger} from '@mintdata/mint-kit/api/log/mk';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {serialize} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import bind from 'bind-decorator';
import {action} from 'mobx';

const logger = Logger.getLogger(Category.Multiplayer);

const LIVE_VALUES_THROTTLE_MILLIS = 1000;

type ThrottledSlotInfo = {
    timerId:any;
    newValue:IValue;
    hasNewValue:boolean;
}

export class LazyValuesSyncController
{
    private connected:boolean = false;

    private pendingSyncEventsFromServer:Array<SyncValueEvent> = null;

    private throttledSlotInfos:Map<String, ThrottledSlotInfo> = null;

    public init()
    {
        const {multiplayer, spreadsheets, openDesign} = controllers.ui;
        const {sheet} = spreadsheets;

        EventBus.listen(sheet, {
            onSlotValueChanged: this._onSlotValueChanged
        });

        EventBus.listen(multiplayer, {
            onConnected: this._onMultiplayerConnected,
            onDisconnected: this._onMultiplayerDisconnected,
            onConnectionLost: this._onConnectionLost,
            onConnectionRestored: this._onConnectionRestored
        });

        EventBus.listen(openDesign, {
            onInitialized: this._onDesignInitialized
        });
    }

    @bind
    private _onMultiplayerConnected()
    {
        this._onConnectionRestored();
    }

    @bind
    private _onMultiplayerDisconnected()
    {
        this._onConnectionLost();
    }

    @bind
    private _onConnectionLost()
    {
        this.connected = false;
        this.pendingSyncEventsFromServer = null;

        if( this.throttledSlotInfos ) 
            this.throttledSlotInfos.forEach(info => clearTimeout(info.timerId));
            
        this.throttledSlotInfos = null;
    }

    @bind
    private _onConnectionRestored()
    {
        const {multiplayer} = controllers.ui;

        this.connected = true;
        this.throttledSlotInfos = new Map();

        multiplayer.subscribeToEvents('init', this._onInitEventFromServer);
        multiplayer.subscribeToEvents('sync-value', this._onSlotValueEventFromServer);
    }

    @bind
    private _onSlotValueChanged(slot:Slot, newValue:IValue)
    {
        const isLazy = slot.isLazy;
        const isLive = slot.isLive;
        const syncableSlot = isLazy || isLive;
        if( !this.connected || !syncableSlot )
            return;

        logger.debug(`MP: New lazy slot value to sync for slot: `, slot, newValue);

        if( isLive )
            this._throttleLiveSlotValue(slot, newValue);
        else
            this._sendSlotValue(slot, newValue);
    }

    private _throttleLiveSlotValue(slot:Slot, newValue:IValue)
    {
        const existingSlotInfo = this.throttledSlotInfos.get(slot.id);
        if( existingSlotInfo )
        {
            existingSlotInfo.newValue = newValue;
            existingSlotInfo.hasNewValue = true;
            return;
        }

        this._sendSlotValue(slot, newValue);

        const newSlotInfo = {} as ThrottledSlotInfo;
        newSlotInfo.timerId = setTimeout(() =>
        {
            if( newSlotInfo.hasNewValue )
                this._sendSlotValue(slot, newSlotInfo.newValue);

            this.throttledSlotInfos.delete(slot.id);

        }, LIVE_VALUES_THROTTLE_MILLIS);

        this.throttledSlotInfos.set(slot.id, newSlotInfo);
    }

    private _sendSlotValue(slot:Slot, newValue:IValue)
    {
        const {multiplayer} = controllers.ui;
        logger.debug(`MP: Sending lazy slot value to server: slotId: ${slot.id}, value: `, newValue);

        // noinspection JSIgnoredPromiseFromCall
        multiplayer.sendEvent('sync-value', {
            sheetId: slot.sheet.id,
            slotId: slot.id,
            value: newValue == null ? null : serialize(newValue)
        });
    }

    @action.bound
    private _onInitEventFromServer(initEvent:InitEvent)
    {
        this.pendingSyncEventsFromServer = initEvent.lastSyncValueEventsBySlotId &&
            Object.values(initEvent.lastSyncValueEventsBySlotId);
    }

    @action.bound
    private _onDesignInitialized()
    {
        if( this.connected && this.pendingSyncEventsFromServer )
            this.pendingSyncEventsFromServer.forEach(
                this._onSlotValueEventFromServer
            );
    }

    @action.bound
    private _onSlotValueEventFromServer(syncEvent:SyncValueEvent)
    {
        logger.debug(`MP: Received new SyncValueEvent: `, syncEvent);

        const {sheet} = controllers.ui.spreadsheets;

        const {sheetId, slotId, value: serializedValue} = syncEvent;
        const slot = sheet.findSlotById(sheetId, slotId);
        if( slot )
        {
            slot.setValue(
                serializedValue == null ? null : Type.deserializeIValue(serializedValue)
            );
        }
    }
}
